import React, { useState, useCallback, useMemo } from "react";
import { Box, Stack, Text } from "@mantine/core";

import { PageProps } from "@sdflc/react-router-pages";

import { DisplayActivation, DisplayActivationInput } from "@/utils/api";

import { DashboardLayout } from "@/components/layouts";

import {
  AppToolbar,
  AppButton,
  ConfirmModal,
  EmptyState,
} from "@/components/generic";
import {
  DisplayActivationsTable,
  DisplayActivationFormModal,
  ConnectScreenInstructionsModal,
} from "@/components/features";
import { useSignalR, useDisplayActivations } from "@/hooks";

export const ScreensPage: React.FC<PageProps> = (props: PageProps) => {
  // We only need sendCommand (a GraphQL mutation) here — do NOT open a TV
  // SignalR connection. Auto-connecting would negotiate as a display and fail
  // with "Missing activationId cookie" on this admin page.
  const { sendCommand } = useSignalR({ autoConnect: false });

  // Use the display activations hook for all data management
  const {
    // Data state
    displayActivations,
    loading,
    loadingMore,
    hasMore,

    // Search and filters
    sortBy,

    // Operations state
    isUpdating,
    removingActivationId,
    selectedActivation,

    // Data operations
    loadMore,
    refresh,

    // Search and filter operations
    setSortBy,

    // CRUD operations
    update,
    remove,

    // UI state management
    setSelectedActivation,

    // Error handling
    lastError,
    clearError,
  } = useDisplayActivations({
    mode: "list",
    autoLoad: true,
    initialSort: [{ key: "pingedAt", direction: "desc" }],
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [modalInstructionOpened, setModalInstructionOpened] =
    useState<boolean>(false);
  const [activationToRemove, setActivationToRemove] =
    useState<DisplayActivation | null>(null);

  // Filter only activated screens (those with activatedAt)
  const screens = useMemo(
    () => displayActivations.filter((screen) => screen.activatedAt),
    [displayActivations],
  );

  // Handle edit activation
  const handleEditActivation = useCallback(
    (activation: DisplayActivation) => {
      setSelectedActivation(activation);
      setFormModalOpened(true);
    },
    [setSelectedActivation],
  );

  // Handle remove activation button click
  const handleRemoveActivation = useCallback(
    (activation: DisplayActivation) => {
      setActivationToRemove(activation);
    },
    [],
  );

  // Handle confirm delete
  const handleConfirmRemove = useCallback(async () => {
    if (activationToRemove?.id) {
      const success = await remove(activationToRemove.id);
      if (success) {
        setActivationToRemove(null);
      }
    }
  }, [activationToRemove, remove]);

  // Handle cancel delete
  const handleCancelRemove = useCallback(() => {
    setActivationToRemove(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedActivation(null);
    clearError();
  }, [setSelectedActivation, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle activation update
  const handleUpdateDisplayActivation = useCallback(
    async (activationId: string, activationData: DisplayActivationInput) => {
      const result = await update(activationId, activationData);
      if (result) {
        setFormModalOpened(false);
        setSelectedActivation(null);
      }
      return result;
    },
    [update, setSelectedActivation],
  );

  const handleReload = useCallback(
    (activation: DisplayActivation) => {
      if (activation?.id) {
        sendCommand({
          command: "reload",
          activationId: [activation.id],
        });
      }
    },
    [sendCommand],
  );

  const handleAddScreen = useCallback(() => {
    setModalInstructionOpened(true);
  }, []);

  return (
    <DashboardLayout {...props}>
      <Stack gap={"md"}>
        {/* Toolbar with stats and actions */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "stats",
              items: [
                <AppButton
                  key="add-screen"
                  iconName="Plus"
                  onClick={handleAddScreen}
                  disabled={loading}
                >
                  Add
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "actions",
              items: [
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "right",
            },
          ]}
        />

        {lastError && (
          <Box p="xl" style={{ textAlign: "center" }}>
            <Stack align="center" gap="md">
              <Text c="red" fw={500}>
                {lastError}
              </Text>
              <AppButton onClick={handleRefresh} variant="secondary">
                Try Again
              </AppButton>
            </Stack>
          </Box>
        )}

        <DisplayActivationsTable
          activations={screens}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onRemoveActivation={handleRemoveActivation}
          onEditActivation={handleEditActivation}
          onReload={handleReload}
          removingActivationId={removingActivationId}
          editedItemId={selectedActivation?.id}
          hideOnNoRecords={true}
        />

        {/* Empty state for no venue selected */}
        <EmptyState
          display={screens.length == 0 && !loading}
          title="No Connected Devices"
          description="To connect a device click the Add button and follow the instructions."
          actionIcon="Plus"
          actionLabel="Add"
          onAction={handleAddScreen}
        />

        {/* Edit Display Activation Form Modal */}
        <DisplayActivationFormModal
          opened={formModalOpened}
          onClose={handleFormModalClose}
          displayActivation={selectedActivation}
          onUpdateDisplayActivation={handleUpdateDisplayActivation}
          isUpdating={isUpdating}
          lastError={lastError}
          onClearError={clearError}
        />

        {/* Remove Confirmation Modal */}
        <ConfirmModal
          opened={!!activationToRemove}
          onClose={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          title="Remove Screen Connection"
          message="Are you sure you want to remove this screen connection?"
          details="This will disconnect the screen and it will need to be reconnected using a new activation code."
          confirmLabel="Delete"
          type="danger"
          confirmInProgress={removingActivationId === activationToRemove?.id}
        />

        <ConnectScreenInstructionsModal
          opened={modalInstructionOpened}
          onClose={() => setModalInstructionOpened(false)}
        />
      </Stack>
    </DashboardLayout>
  );
};
